import React from "react";
import { Box, Typography, styled } from "@mui/material";
import Link from "next/link";
import { primary } from "../../../constants/theme";

const CustomSeoContentRenderer = styled(Box)(({ theme }) => ({
  width: "100%",
  margin: "0",
  ".content-heading2": {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "15px",
    marginTop: "30px",
    color: primary.black,
  },
  ".content-heading3": {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "10px",
    marginTop: "20px",
    color: primary.black,
    "& a": {
      textDecoration: "underline",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  ".content-text": {
    fontSize: "16px",
    marginBottom: "10px",
    fontWeight: "400",
    color: primary.black,
    lineHeight: 1.6,
  },
  ".bullet-list": {
    paddingLeft: "30px",
    marginLeft: "20px",
    marginBottom: "15px",
    listStyleType: "disc",
    "& li": {
      fontSize: "16px",
      marginBottom: "8px",
      fontWeight: "400",
      color: primary.black,
      lineHeight: 1.6,
      listStyleType: "disc",
      display: "list-item",
    },
  },
  ".numbered-list": {
    paddingLeft: "30px",
    marginLeft: "20px",
    marginBottom: "15px",
    listStyleType: "none",
    counterReset: "list-counter",
    "& li": {
      fontSize: "16px",
      marginBottom: "8px",
      fontWeight: "400",
      color: primary.black,
      lineHeight: 1.6,
      listStyleType: "none",
      display: "list-item",
      counterIncrement: "list-counter",
      position: "relative",
      paddingLeft: "0",
    },
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    ".content-heading2": {
      fontSize: "20px",
    },
    ".content-heading3": {
      fontSize: "18px",
    },
    ".content-text": {
      fontSize: "14px",
    },
    ".bullet-list": {
      "& li": {
        fontSize: "14px",
      },
    },
    ".numbered-list": {
      "& li": {
        fontSize: "14px",
      },
    },
  },
  [theme.breakpoints.down("sm")]: {
    ".content-heading2": {
      fontSize: "18px",
    },
    ".content-heading3": {
      fontSize: "16px",
    },
    ".content-text": {
      fontSize: "12px",
    },
    ".bullet-list": {
      "& li": {
        fontSize: "12px",
      },
    },
    ".numbered-list": {
      "& li": {
        fontSize: "12px",
      },
    },
  },
}));

const SeoContentRenderer = ({ content }) => {
  if (!content || !Array.isArray(content)) {
    return null;
  }

  return (
    <CustomSeoContentRenderer>
      {content.map((section, sectionIndex) => {
        if (!section || typeof section !== "object") return null;

        return (
          <Box key={sectionIndex}>
            {/* Main Heading (h2) */}
            {section.heading && (
              <Typography variant="h2" className="content-heading2">
                {section.heading}
              </Typography>
            )}

            {/* Paragraphs */}
            {section.paragraphs &&
              Array.isArray(section.paragraphs) &&
              section.paragraphs.map((paragraph, paraIndex) => (
                <Typography key={paraIndex} className="content-text">
                  {paragraph}
                </Typography>
              ))}

            {/* Sub-sections */}
            {section.subSections &&
              Array.isArray(section.subSections) &&
              section.subSections.map((subSection, subIndex) => (
                <Box key={subIndex}>
                  {/* Sub-heading (h3) */}
                  {subSection.heading && (
                    <Typography variant="h3" className="content-heading3">
                      {subSection.link ? (
                        <Link
                          href={subSection.link}
                          style={{
                            color: primary.black,
                            fontWeight: "inherit",
                          }}
                        >
                          {subSection.heading}
                        </Link>
                      ) : (
                        subSection.heading
                      )}
                    </Typography>
                  )}

                  {/* Sub-section paragraphs */}
                  {subSection.paragraphs &&
                    Array.isArray(subSection.paragraphs) &&
                    subSection.paragraphs.map((paragraph, paraIndex) => (
                      <Typography key={paraIndex} className="content-text">
                        {paragraph}
                      </Typography>
                    ))}

                  {/* Bullet list */}
                  {subSection.list && Array.isArray(subSection.list) && (
                    <ul className="bullet-list">
                      {subSection.list.map((item, itemIndex) => (
                        <li key={itemIndex}>
                          {typeof item === "string" ? (
                            item
                          ) : (
                            <>
                              {item.label && <strong>{item.label}:</strong>}{" "}
                              {item.text}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Box>
              ))}

            {/* List at section level - ordered or unordered */}
            {section.list && Array.isArray(section.list) && (
              section.listType === "ordered" ? (
                <ol className="numbered-list">
                  {section.list.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      {typeof item === "string" ? (
                        item
                      ) : (
                        <>
                          {item.label && <strong>{item.label}:</strong>} {item.text}
                        </>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <ul className="bullet-list">
                  {section.list.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      {typeof item === "string" ? (
                        item
                      ) : (
                        <>
                          {item.label && <strong>{item.label}:</strong>} {item.text}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )
            )}

            {/* Additional paragraphs after sub-sections */}
            {section.additionalParagraphs &&
              Array.isArray(section.additionalParagraphs) &&
              section.additionalParagraphs.map((paragraph, paraIndex) => (
                <Typography key={`additional-${paraIndex}`} className="content-text">
                  {paragraph}
                </Typography>
              ))}
          </Box>
        );
      })}
    </CustomSeoContentRenderer>
  );
};

export default SeoContentRenderer;

